import React from 'react'
import { useFormikContext, useField, Field } from 'formik'
import pluralize from 'pluralize'
import { startCase } from 'lodash'

import Select from '@/components/forms/Select'
import { NumberField } from '@/components/forms/formik/NumberField'
import { FormField } from '@/components/forms/FormField'

const frequencyOptions = [
  { value: 'monthly', label: 'Per month' },
  { value: 'weekly', label: 'Per week' },
]

const getDistributionOptions = (frequency) => {
  const periodLabel = frequency === 'weekly' ? 'week' : 'month'
  return [
    {
      value: true,
      label: 'Distribute evenly (default)',
      description: `Scoring is spread evenly throughout the ${periodLabel} to meet your goal.`,
    },
    {
      value: false,
      label: 'Score as early as possible',
      description: `The first eligible calls are scored early in the ${periodLabel}, up to your goal.`,
    },
  ]
}

export const QACopilotScoringStrategyInputs = ({ isOutdatedScorecard, callType }) => {
  const { setFieldValue } = useFormikContext()
  const [strategyField] = useField('distribute_evenly')
  const [frequencyField] = useField('scorecard_frequency')
  const [, scoreCountMeta] = useField('required_score_count')

  const handleSelectChange = async (selectedOption) => {
    await setFieldValue('distribute_evenly', selectedOption.value)
  }

  const handleFrequencyChange = async (selectedOption) => {
    await setFieldValue('scorecard_frequency', selectedOption.value)
  }

  const frequencyLabel = frequencyField.value === 'weekly' ? 'per week' : 'per month'
  const distributionOptions = getDistributionOptions(frequencyField.value)

  return (
    <div className="multiple-field-container horizontal scoring-goal-fields">
      <Field
        name="required_score_count"
        component={NumberField}
        label={`${pluralize(startCase(callType))} ${frequencyLabel}`}
        errorMessage={scoreCountMeta.error}
        disabled={isOutdatedScorecard}
      />

      <FormField className="frequency-field">
        <label>Scoring frequency</label>
        <Select
          options={frequencyOptions}
          value={frequencyField.value}
          onChange={handleFrequencyChange}
          placeholder="Select frequency"
          data-testid="scoring-frequency-dropdown"
          isClearable={false}
          isDisabled={isOutdatedScorecard}
        />
      </FormField>

      <FormField className="strategy-field">
        <label>Scoring distribution strategy</label>
        <Select
          options={distributionOptions}
          value={strategyField.value}
          onChange={handleSelectChange}
          placeholder="Select strategy"
          data-testid="scoring-strategy-dropdown"
          isClearable={false}
          isDisabled={isOutdatedScorecard}
        />
      </FormField>

      {strategyField.value === true && frequencyField.value === 'monthly' && (
        <FormField className="cutoff-day-field" data-testid="monthly-cutoff-day">
          <Field
            name="monthly_cutoff_day"
            component={NumberField}
            label="Complete by day (optional)"
            placeholder="e.g. 25"
            min={1}
            max={31}
            step={1}
            disabled={isOutdatedScorecard}
          />
          <p className="cutoff-day-field__hint">
            Scoring is completed by this day of the month, leaving the rest of the month to review,
            handle challenges, and finalize manual scoring. Leave blank to spread scoring across the
            whole month.
          </p>
        </FormField>
      )}
    </div>
  )
}
